import createFetchClient from "openapi-fetch";
import type { paths } from "../generated/schema";
import { SESSION_TOKEN_KEY } from "./use-session-token";

// Pluggable config — call before any API requests are made (e.g. at app init in baback-office).
// In customer-facing contexts (standalone widget) these defaults are used as-is.
let _getMerchantToken: () => string | null = () => null;
let _baseUrl = "https://api2.baback.co/";

export function configureMerchantToken(getter: () => string | null) {
  _getMerchantToken = getter;
}

export function configureReturnWidgetBaseUrl(url: string) {
  _baseUrl = url;
  _client = null; // invalidate so next call rebuilds with new URL
}

let _client: ReturnType<typeof createFetchClient<paths>> | null = null;

function buildClient() {
  return createFetchClient<paths>({
    baseUrl: _baseUrl,
    credentials: "include",
    fetch: async (input, init = {}) => {
      // openapi-fetch 0.17+ passes a fully-built Request object as `input` with all
      // headers (Content-Type, Authorization, etc.) already set. `init` is undefined
      // in browser contexts. Reading from `init.headers` gives an empty Headers object
      // and overrides the request headers — stripping Content-Type and breaking body parsing.
      const sourceHeaders = input instanceof Request ? input.headers : (init as RequestInit).headers;
      const headers = new Headers(sourceHeaders);

      // Inject auth token when the caller didn't set one explicitly.
      // Priority: merchant token (admin preview) → session token (customer JWT).
      const existing = headers.get("authorization") ?? "";
      if (!existing || existing === "Bearer ") {
        const merchantToken = _getMerchantToken();
        const sessionToken = localStorage.getItem(SESSION_TOKEN_KEY) ?? "";
        const token = merchantToken || sessionToken || null;
        if (token) {
          headers.set("authorization", `Bearer ${token}`);
        }
      }

      const response = await fetch(input as RequestInfo, {
        ...(init as RequestInit),
        headers,
      });

      // Store the order session JWT for subsequent calls.
      const data = await response.clone().json().catch(() => null);
      const token = data?.token ?? data?.data?.token;
      if (token) {
        localStorage.setItem(SESSION_TOKEN_KEY, token.slice(0, -12));
      }

      return response;
    },
  });
}

export function getReturnWidgetFetchClient() {
  if (!_client) _client = buildClient();
  return _client;
}

export function getReturnWidgetBaseUrl() {
  return _baseUrl;
}

/**
 * Returns the Authorization header for public widget endpoints.
 * Uses only the customer session token — merchant token is intentionally excluded
 * because endpoints like /public/return-request/search reject it with 401.
 * When the session token is absent the header is "Bearer " (empty), which causes
 * the fetch middleware to fall back to the merchant token for the initial request
 * (e.g. /public/orders/load-with-items before any session is established).
 */
export function getPublicAuthHeader() {
  const sessionToken = localStorage.getItem(SESSION_TOKEN_KEY) ?? "";
  return { authorization: `Bearer ${sessionToken}` };
}
